import React, { useState, useMemo } from 'react';
import { Download, Calendar, X, FileSpreadsheet } from 'lucide-react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';

const ExportExcelNhapBaoBi = ({ packagingData, phanBoData, onClose }) => {
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [exportType, setExportType] = useState('year'); // 'year' hoặc 'month'
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [isExporting, setIsExporting] = useState(false);

    // Lấy danh sách các năm có dữ liệu
    const availableYears = useMemo(() => {
        const years = new Set();
        packagingData.forEach(record => {
            const year = new Date(record['NGÀY THÁNG']).getFullYear();
            if (!isNaN(year)) years.add(year);
        });
        return Array.from(years).sort((a, b) => b - a);
    }, [packagingData]);

    // Lấy danh sách các tháng có dữ liệu trong năm đã chọn
    const availableMonths = useMemo(() => {
        const months = new Set();
        packagingData.forEach(record => {
            const date = new Date(record['NGÀY THÁNG']);
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            if (year === parseInt(selectedYear)) {
                months.add(month);
            }
        });
        return Array.from(months).sort((a, b) => a - b);
    }, [packagingData, selectedYear]);

    const formatNumber = (number) => {
        if (number === null || number === undefined || number === '') return 0;
        const num = parseInt(number, 10);
        if (isNaN(num)) return 0;
        return num;
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatDateDisplay = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const handleExport = async () => {
        if (!selectedYear) {
            toast.warning('Vui lòng chọn năm để xuất');
            return;
        }

        if (exportType === 'month' && !selectedMonth) {
            toast.warning('Vui lòng chọn tháng để xuất');
            return;
        }

        setIsExporting(true);

        try {
            if (exportType === 'year') {
                await exportByYear();
            } else {
                await exportByMonth();
            }
        } catch (error) {
            console.error('Error exporting Excel:', error);
            toast.error('Có lỗi xảy ra khi xuất file Excel');
        } finally {
            setIsExporting(false);
        }
    };

    const exportByYear = async () => {
        // Lọc dữ liệu theo năm
        const yearData = packagingData.filter(record => {
            const year = new Date(record['NGÀY THÁNG']).getFullYear();
            return year === parseInt(selectedYear);
        });

        if (yearData.length === 0) {
            toast.warning(`Không có dữ liệu cho năm ${selectedYear}`);
            return;
        }

        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'Hoàng Nguyên Long';
        workbook.created = new Date();

        // Tạo sheet cho từng tháng (1-12)
        for (let month = 1; month <= 12; month++) {
            const monthData = yearData.filter(record => {
                const recordMonth = new Date(record['NGÀY THÁNG']).getMonth() + 1;
                return recordMonth === month;
            });

            if (monthData.length > 0) {
                await createMonthSheet(workbook, monthData, month, selectedYear);
            }
        }

        // Tạo sheet tổng hợp
        await createSummarySheet(workbook, yearData, selectedYear);

        // Xuất file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        saveAs(blob, `BAO_BI_NAM_${selectedYear}.xlsx`);
        
        toast.success(`Xuất file Excel năm ${selectedYear} thành công!`);
        onClose();
    };

    const exportByMonth = async () => {
        // Lọc dữ liệu theo tháng
        const monthData = packagingData.filter(record => {
            const date = new Date(record['NGÀY THÁNG']);
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            return year === parseInt(selectedYear) && month === parseInt(selectedMonth);
        });

        if (monthData.length === 0) {
            toast.warning(`Không có dữ liệu cho tháng ${selectedMonth}/${selectedYear}`);
            return;
        }

        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'Hoàng Nguyên Long';
        workbook.created = new Date();

        // Tạo sheet cho tháng đã chọn
        await createMonthSheet(workbook, monthData, selectedMonth, selectedYear);

        // Xuất file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        saveAs(blob, `BAO_BI_T${selectedMonth.toString().padStart(2, '0')}_${selectedYear}.xlsx`);
        
        toast.success(`Xuất file Excel tháng ${selectedMonth}/${selectedYear} thành công!`);
        onClose();
    };

    const createMonthSheet = async (workbook, monthData, month, year) => {
        const sheetName = `T${month.toString().padStart(2, '0')},${year.toString().slice(-2)}`;
        const worksheet = workbook.addWorksheet(sheetName);

        // Set column widths
        worksheet.columns = [
            { width: 6 },   // STT
            { width: 12 },  // NGÀY THÁNG
            { width: 13 },  // SỐ XE
            { width: 15 },  // TÊN HÀNG
            { width: 35 },  // KHÁCH HÀNG
            { width: 13 },  // SỐ KG NHẬN BB
            { width: 13 },  // SỐ KG TRỪ %
            { width: 13 },  // SỐ KG THỰC NHẬN
            { width: 15 },  // NƠI NHẬN
            { width: 10 },  // SỐ DÂY
            { width: 25 },  // GHI CHÚ
            { width: 15 }   // TỔNG NHẬN TRONG NGÀY
        ];

        // Header - 3 dòng đầu
        worksheet.addRow(['Tên ĐV: ', 'Công Ty CP TM SX  XD Hoàng Nguyên Long']);
        worksheet.addRow(['Địa Chỉ', 'Số 389 Nguyễn Đức Thuận , Hiệp Thành, TDM , Bình Dương']);
        worksheet.addRow([`BAO BÌ NHẬP VỀ THÁNG ${month.toString().padStart(2, '0')}/${year}`]);

        // Merge và style header
        worksheet.mergeCells('B1:L1');
        worksheet.mergeCells('B2:L2');
        worksheet.mergeCells('A3:L3');

        // Style cho header
        ['A1', 'B1', 'A2', 'B2', 'A3'].forEach(cell => {
            worksheet.getCell(cell).font = { name: 'Arial', size: 11, bold: true };
            worksheet.getCell(cell).alignment = { horizontal: 'left', vertical: 'middle' };
        });
        worksheet.getCell('A3').font = { name: 'Arial', size: 13, bold: true };

        // Dòng trống
        worksheet.addRow([]);

        // Header bảng
        const headerRow = worksheet.addRow([
            'STT',
            'NGÀY THÁNG',
            'SỐ XE',
            'TÊN HÀNG',
            'KHÁCH HÀNG',
            'SỐ KG\nNHẬN BB',
            'SỐ KG TRỪ %\n(3%)',
            'SỐ KG\nTHỰC NHẬN',
            'NƠI NHẬN',
            'SỐ DÂY',
            'GHI CHÚ',
            'TỔNG NHẬN\nTRONG NGÀY'
        ]);

        // Style header bảng
        headerRow.height = 30;
        headerRow.eachCell((cell) => {
            cell.font = { name: 'Arial', size: 11, bold: true };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFD3D3D3' }
            };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        // Nhóm dữ liệu theo ngày
        const groupedByDate = {};
        monthData.forEach(record => {
            const dateStr = formatDateForInput(record['NGÀY THÁNG']);
            if (!groupedByDate[dateStr]) {
                groupedByDate[dateStr] = [];
            }
            groupedByDate[dateStr].push(record);
        });

        const sortedDates = Object.keys(groupedByDate).sort();

        let totalNhapBB = 0;
        let totalTruBB = 0;
        let totalThucNhanBB = 0;

        sortedDates.forEach(dateStr => {
            const records = groupedByDate[dateStr];
            const formattedDate = formatDateDisplay(dateStr);
            const dailyTotal = records.reduce((sum, r) => sum + formatNumber(r['NHẬP BB (KG)']), 0);

            let sttCounter = 1;
            const dayStartRow = worksheet.lastRow.number + 1;

            records.forEach((record) => {
                const nhapBB = formatNumber(record['NHẬP BB (KG)']);
                const truBB = formatNumber(record['TRỪ BB (KG)']);
                const thucNhanBB = formatNumber(record['THỰC NHẬN BB (KG)']);

                totalNhapBB += nhapBB;
                totalTruBB += truBB;
                totalThucNhanBB += thucNhanBB;

                const allocations = phanBoData.filter(pb => pb.ID_NHAPBAOBI === record.ID);
                const totalAllocatedBBA = allocations.reduce((sum, alloc) => sum + formatNumber(alloc['KHỐI LƯỢNG']), 0);
                const remainingBBE = thucNhanBB - totalAllocatedBBA;

                const vehicleStartRow = worksheet.lastRow.number + 1;

                if (allocations.length > 0) {
                    // Có phân bổ BBA
                    allocations.forEach((alloc, allocIndex) => {
                        const row = worksheet.addRow([
                            allocIndex === 0 ? sttCounter : '',
                            allocIndex === 0 ? formattedDate : '',
                            allocIndex === 0 ? record['SỐ XE'] : '',
                            alloc['TÊN HÀNG'] || 'Bao bì anh',
                            allocIndex === 0 ? record['NCC'] : '',
                            allocIndex === 0 ? nhapBB : '',
                            allocIndex === 0 ? truBB : '',
                            formatNumber(alloc['KHỐI LƯỢNG']),
                            alloc['TỔ'] || '',
                            alloc['SỐ DÂY'] || '',
                            allocIndex === 0 ? (record['GHI CHÚ'] || '') : '',
                            ''
                        ]);
                        styleDataRow(row);
                    });

                    // Nếu còn dư BBE
                    if (remainingBBE > 0) {
                        const row = worksheet.addRow([
                            '',
                            '',
                            '',
                            'Bao bì em',
                            '',
                            '',
                            '',
                            remainingBBE,
                            'Bãi',
                            '',
                            '',
                            ''
                        ]);
                        styleDataRow(row);
                    }
                } else {
                    // Không có phân bổ BBA
                    const row = worksheet.addRow([
                        sttCounter,
                        formattedDate,
                        record['SỐ XE'],
                        'Bao bì em',
                        record['NCC'],
                        nhapBB,
                        truBB,
                        thucNhanBB,
                        'Bãi',
                        '',
                        record['GHI CHÚ'] || '',
                        ''
                    ]);
                    styleDataRow(row);
                }

                const vehicleEndRow = worksheet.lastRow.number;
                const rowsForVehicle = vehicleEndRow - vehicleStartRow + 1;

                // Merge cells cho xe
                if (rowsForVehicle > 1) {
                    worksheet.mergeCells(vehicleStartRow, 1, vehicleEndRow, 1); // STT
                    worksheet.mergeCells(vehicleStartRow, 2, vehicleEndRow, 2); // NGÀY THÁNG
                    worksheet.mergeCells(vehicleStartRow, 3, vehicleEndRow, 3); // SỐ XE
                    worksheet.mergeCells(vehicleStartRow, 5, vehicleEndRow, 5); // KHÁCH HÀNG
                    worksheet.mergeCells(vehicleStartRow, 6, vehicleEndRow, 6); // SỐ KG NHẬN BB
                    worksheet.mergeCells(vehicleStartRow, 7, vehicleEndRow, 7); // SỐ KG TRỪ %
                    worksheet.mergeCells(vehicleStartRow, 11, vehicleEndRow, 11); // GHI CHÚ

                    // Căn giữa cho các ô merge
                    [1, 2, 3].forEach(col => {
                        worksheet.getCell(vehicleStartRow, col).alignment = { 
                            horizontal: 'center', 
                            vertical: 'middle' 
                        };
                    });
                }

                sttCounter++;
            });

            // Merge và điền TỔNG NHẬN TRONG NGÀY
            const dayEndRow = worksheet.lastRow.number;
            const totalRowsForDay = dayEndRow - dayStartRow + 1;

            if (totalRowsForDay > 0) {
                worksheet.getCell(dayStartRow, 12).value = dailyTotal;
                
                if (totalRowsForDay > 1) {
                    worksheet.mergeCells(dayStartRow, 12, dayEndRow, 12);
                }
                
                worksheet.getCell(dayStartRow, 12).alignment = { 
                    horizontal: 'center', 
                    vertical: 'middle' 
                };
                worksheet.getCell(dayStartRow, 12).numFmt = '#,##0';
            }
        });

        // Dòng tổng cộng
        const totalRow = worksheet.addRow([
            '',
            'TỔNG CỘNG',
            '',
            '',
            '',
            totalNhapBB,
            totalTruBB,
            totalThucNhanBB,
            '',
            '',
            '',
            ''
        ]);

        // Merge và style dòng tổng
        worksheet.mergeCells(totalRow.number, 1, totalRow.number, 2);
        
        totalRow.eachCell((cell, colNumber) => {
            cell.font = { name: 'Arial', size: 11, bold: true };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFFFF00' }
            };

            if (colNumber === 1 || colNumber === 2) {
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            } else if (colNumber === 6 || colNumber === 7 || colNumber === 8) {
                cell.alignment = { horizontal: 'right', vertical: 'middle' };
                cell.numFmt = '#,##0';
            }
        });
    };

    const styleDataRow = (row) => {
        row.eachCell((cell, colNumber) => {
            cell.font = { name: 'Arial', size: 11 };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };

            // Căn giữa: STT, NGÀY THÁNG, SỐ XE, TÊN HÀNG, NƠI NHẬN, SỐ DÂY
            if ([1, 2, 3, 4, 9, 10].includes(colNumber)) {
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            }
            // Căn phải và format số: SỐ KG NHẬN BB, SỐ KG TRỪ %, SỐ KG THỰC NHẬN
            else if ([6, 7, 8].includes(colNumber)) {
                cell.alignment = { horizontal: 'right', vertical: 'middle' };
                if (cell.value) cell.numFmt = '#,##0';
            }
            // Căn trái: KHÁCH HÀNG, GHI CHÚ
            else {
                cell.alignment = { horizontal: 'left', vertical: 'middle' };
            }
        });
    };

    const createSummarySheet = async (workbook, yearData, year) => {
        const worksheet = workbook.addWorksheet(`TH${year}`);

        // Set column widths
        worksheet.columns = [
            { width: 8 },   // STT
            { width: 20 },  // NGÀY THÁNG
            { width: 15 },  // SỐ KG NHẬN BB
            { width: 15 },  // SỐ KG TRỪ %
            { width: 15 }   // SỐ KG THỰC NHẬN
        ];

        // Header
        worksheet.addRow([`Sheet: TH${year}`]);
        worksheet.addRow(['Tên ĐV: ', 'Công Ty CP TM SX  XD Hoàng Nguyên Long']);
        worksheet.addRow(['Địa Chỉ', 'Số 389 Nguyễn Đức Thuận , Hiệp Thành, TDM , Bình Dương']);
        worksheet.addRow(['Xưởng cưa Hoàng Nguyên Long .DH507/Số 327 Ấp Kỉnh Nhượng,']);
        worksheet.addRow(['Xã Vĩnh Hòa, Huyện Phú Giáo, Tỉnh Bình Dương']);
        worksheet.addRow([`BAO BÌ NHẬP VỀ THÁNG 01/${year}`]);
        worksheet.addRow([]);

        // Style header
        for (let i = 1; i <= 6; i++) {
            worksheet.getRow(i).font = { name: 'Arial', size: i === 6 ? 13 : 11, bold: true };
            worksheet.getRow(i).alignment = { horizontal: 'left', vertical: 'middle' };
        }

        // Header bảng
        const headerRow = worksheet.addRow([
            'STT',
            'NGÀY THÁNG',
            'SỐ KG\nNHẬN BB',
            'SỐ KG TRỪ %\n(5%)',
            'SỐ KG\nTHỰC NHẬN'
        ]);

        headerRow.height = 30;
        headerRow.eachCell((cell) => {
            cell.font = { name: 'Arial', size: 11, bold: true };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFD3D3D3' }
            };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        let totalNhapBB = 0;
        let totalTruBB = 0;
        let totalThucNhanBB = 0;

        // Dữ liệu từng tháng
        for (let month = 1; month <= 12; month++) {
            const monthData = yearData.filter(record => {
                const recordMonth = new Date(record['NGÀY THÁNG']).getMonth() + 1;
                return recordMonth === month;
            });

            let monthNhapBB = '';
            let monthTruBB = '';
            let monthThucNhanBB = '';

            if (monthData.length > 0) {
                monthNhapBB = monthData.reduce((sum, r) => sum + formatNumber(r['NHẬP BB (KG)']), 0);
                monthTruBB = monthData.reduce((sum, r) => sum + formatNumber(r['TRỪ BB (KG)']), 0);
                monthThucNhanBB = monthData.reduce((sum, r) => sum + formatNumber(r['THỰC NHẬN BB (KG)']), 0);

                totalNhapBB += monthNhapBB;
                totalTruBB += monthTruBB;
                totalThucNhanBB += monthThucNhanBB;
            }

            const row = worksheet.addRow([
                month,
                `Tháng ${month.toString().padStart(2, '0')}/${year}`,
                monthNhapBB,
                monthTruBB,
                monthThucNhanBB
            ]);

            row.eachCell((cell, colNumber) => {
                cell.font = { name: 'Arial', size: 11 };
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };

                if (colNumber === 1 || colNumber === 2) {
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                } else {
                    cell.alignment = { horizontal: 'right', vertical: 'middle' };
                    if (cell.value) cell.numFmt = '#,##0';
                }
            });
        }

        // Dòng tổng cộng
        const totalRow = worksheet.addRow([
            '',
            'TỔNG CỘNG',
            totalNhapBB,
            totalTruBB,
            totalThucNhanBB
        ]);

        totalRow.eachCell((cell, colNumber) => {
            cell.font = { name: 'Arial', size: 11, bold: true };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFFFF00' }
            };

            if (colNumber === 1 || colNumber === 2) {
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            } else {
                cell.alignment = { horizontal: 'right', vertical: 'middle' };
                cell.numFmt = '#,##0';
            }
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <FileSpreadsheet className="h-6 w-6 text-green-600" />
                        Xuất Excel Bao Bì
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="space-y-4">
                    {/* Chọn loại xuất */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Loại xuất <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={() => setExportType('year')}
                                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                                    exportType === 'year'
                                        ? 'border-green-600 bg-green-50 text-green-700'
                                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                }`}
                            >
                                <div className="text-center">
                                    <div className="font-semibold">Theo năm</div>
                                    <div className="text-xs mt-1">12 tháng + tổng hợp</div>
                                </div>
                            </button>
                            <button
                                onClick={() => setExportType('month')}
                                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                                    exportType === 'month'
                                        ? 'border-green-600 bg-green-50 text-green-700'
                                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                }`}
                            >
                                <div className="text-center">
                                    <div className="font-semibold">Theo tháng</div>
                                    <div className="text-xs mt-1">1 tháng cụ thể</div>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Chọn năm */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Chọn năm <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                <Calendar className="w-4 h-4 text-gray-500" />
                            </div>
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                className="pl-10 p-2.5 border border-gray-300 rounded-lg w-full focus:ring-green-500 focus:border-green-500"
                            >
                                {availableYears.map(year => (
                                    <option key={year} value={year}>
                                        Năm {year}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Chọn tháng (chỉ hiện khi xuất theo tháng) */}
                    {exportType === 'month' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Chọn tháng <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-green-500 focus:border-green-500"
                            >
                                {availableMonths.length > 0 ? (
                                    availableMonths.map(month => (
                                        <option key={month} value={month}>
                                            Tháng {month.toString().padStart(2, '0')}/{selectedYear}
                                        </option>
                                    ))
                                ) : (
                                    <option value="">Không có dữ liệu</option>
                                )}
                            </select>
                        </div>
                    )}

                    {/* Thông tin */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="text-sm font-medium text-blue-800 mb-2">
                            {exportType === 'year' ? 'Xuất theo năm:' : 'Xuất theo tháng:'}
                        </h3>
                        <ul className="text-xs text-blue-700 space-y-1">
                            {exportType === 'year' ? (
                                <>
                                    <li>✓ Tất cả các tháng có dữ liệu</li>
                                    <li>✓ Sheet tổng hợp cả năm</li>
                                    <li>✓ File: BAO_BI_NAM_{selectedYear}.xlsx</li>
                                </>
                            ) : (
                                <>
                                    <li>✓ Chỉ tháng {selectedMonth}/{selectedYear}</li>
                                    <li>✓ File: BAO_BI_T{selectedMonth.toString().padStart(2, '0')}_{selectedYear}.xlsx</li>
                                    <li>✓ Nhanh hơn, file nhỏ hơn</li>
                                </>
                            )}
                        </ul>
                    </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        disabled={isExporting}
                    >
                        Hủy
                    </button>
                    <button
                        onClick={handleExport}
                        disabled={isExporting || (exportType === 'month' && availableMonths.length === 0)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                    >
                        {isExporting ? (
                            <>
                                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                Đang xuất...
                            </>
                        ) : (
                            <>
                                <Download className="w-4 h-4" />
                                Xuất Excel
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExportExcelNhapBaoBi;
